'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircle, MapPin, Phone, Pickaxe } from 'lucide-react'

const STEPS = [
  {
    icon: Phone,
    step: '1',
    label: 'Call or fill in the form',
    detail: 'Tell us what you need. Takes two minutes.',
  },
  {
    icon: MapPin,
    step: '2',
    label: 'Free on-site quote',
    detail: 'We come to you, assess the site, and give you a price.',
  },
  {
    icon: Pickaxe,
    step: '3',
    label: 'We do the work',
    detail: 'One crew. Excavator, skid steer, tipper.',
  },
  {
    icon: CheckCircle,
    step: '4',
    label: 'Job done, site clean',
    detail: 'We leave it better than we found it.',
  },
] as const

interface ProcessTimelineProps {
  onProgress?: (progress: number) => void
}

export function ProcessTimeline({ onProgress }: ProcessTimelineProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const onProgressRef = useRef(onProgress)

  useEffect(() => {
    onProgressRef.current = onProgress
  }, [onProgress])

  const rafRef = useRef(0)

  const handleScroll = useCallback(() => {
    // Gate with rAF to avoid layout thrashing from getBoundingClientRect
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0
      const el = sectionRef.current
      if (!el) return

      const rect = el.getBoundingClientRect()
      const viewH = window.innerHeight
      // On mobile, use a larger denominator so progress starts earlier
      const span = viewH < 768 ? viewH * 0.85 : viewH * 0.7
      const raw = (viewH - rect.top) / span
      const clamped = Math.max(0, Math.min(1, raw))
      setProgress(clamped)
      onProgressRef.current?.(clamped)
    })
  }, [])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return

    let frame1 = 0

    const attach = () => {
      window.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', handleScroll, { passive: true })
      // Measure after two frames to ensure layout is settled on CSR navigation
      frame1 = requestAnimationFrame(() => {
        frame1 = requestAnimationFrame(() => {
          rafRef.current = 0
          handleScroll()
        })
      })
    }

    const detach = () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      cancelAnimationFrame(frame1)
      cancelAnimationFrame(rafRef.current)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) attach()
        else detach()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      detach()
    }
  }, [handleScroll])

  // Each node activates at 0.15, 0.35, 0.55, 0.75
  const nodeThresholds = [0.15, 0.35, 0.55, 0.75]
  // Scale line so it reaches 100% when the last node activates
  const lineProgress = Math.min(1, progress / 0.75)

  return (
    <div ref={sectionRef}>
      {/* Desktop: horizontal */}
      <div className="hidden md:block">
        <div className="relative mx-auto max-w-5xl px-12">
          {/* SVG line with rough filter, behind nodes */}
          <div className="absolute top-[1.75rem] right-12 left-12 -translate-y-1/2">
            <svg
              viewBox="0 0 1000 14"
              preserveAspectRatio="none"
              className="h-3.5 w-full overflow-visible"
              aria-hidden="true"
            >
              <defs>
                <filter id="rough-line" x="-5%" y="-50%" width="110%" height="200%">
                  <feTurbulence
                    type="turbulence"
                    baseFrequency="0.04 0.08"
                    numOctaves={3}
                    seed={17}
                    stitchTiles="stitch"
                    result="noise"
                  />
                  <feDisplacementMap
                    in="SourceGraphic"
                    in2="noise"
                    scale={4}
                    xChannelSelector="R"
                    yChannelSelector="G"
                  />
                </filter>
                <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2F9E34" />
                  <stop offset="50%" stopColor="#852E85" />
                  <stop offset="100%" stopColor="#99F66E" />
                </linearGradient>
              </defs>
              {/* Track */}
              <rect
                x="0"
                y="5"
                width="1000"
                height="4"
                rx="2"
                fill="rgba(255,255,255,0.08)"
                filter="url(#rough-line)"
              />
              {/* Progress fill */}
              <rect
                x="0"
                y="5"
                width={1000 * lineProgress}
                height="4"
                rx="2"
                fill="url(#line-grad)"
                filter="url(#rough-line)"
                style={{ transition: 'width 0.1s linear' }}
              />
            </svg>
          </div>

          {/* Nodes */}
          <div className="relative grid grid-cols-4 gap-8">
            {STEPS.map((item, i) => {
              const active = progress >= nodeThresholds[i]
              return (
                <div key={item.step} className="flex flex-col items-center text-center">
                  {/* Node circle */}
                  <div
                    className="relative z-20 flex size-14 items-center justify-center rounded-full border-2 transition-all duration-500"
                    style={{
                      borderColor: active ? '#2F9E34' : 'rgba(255,255,255,0.15)',
                      backgroundColor: active ? '#1a1a0e' : '#1a0e20',
                      boxShadow: active
                        ? '0 0 20px rgba(47,158,52,0.3), 0 0 40px rgba(47,158,52,0.1)'
                        : 'none',
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-full transition-opacity duration-500"
                      style={{
                        backgroundColor: 'rgba(47,158,52,0.15)',
                        opacity: active ? 1 : 0,
                      }}
                    />
                    <item.icon
                      className="relative z-10 size-6 transition-colors duration-500"
                      style={{ color: active ? '#99F66E' : 'rgba(255,255,255,0.3)' }}
                      aria-hidden="true"
                    />
                  </div>

                  {/* Step label */}
                  <span
                    className="font-heading mt-4 text-base font-bold tracking-wide uppercase transition-colors duration-500"
                    style={{ color: active ? '#e995e8' : 'rgba(255,255,255,0.25)' }}
                  >
                    Step {item.step}
                  </span>
                  <h3
                    className="font-heading mt-1 text-lg font-bold transition-colors duration-500"
                    style={{ color: active ? '#ffffff' : 'rgba(255,255,255,0.4)' }}
                  >
                    {item.label}
                  </h3>
                  <p
                    className="mt-1 text-base transition-colors duration-500"
                    style={{ color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)' }}
                  >
                    {item.detail}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Mobile/tablet: vertical */}
      <div className="md:hidden">
        <div className="mx-auto max-w-md px-4">
          {/* Nodes. The line is positioned inside this container so it spans
              from the first circle centre to the last circle centre. */}
          <div className="relative flex flex-col gap-12">
            {/* Vertical SVG line. Top and bottom inset by half the circle height
                so the line starts and ends at circle centres. */}
            <div className="absolute top-[1.375rem] bottom-[1.375rem] left-[1.375rem] -translate-x-1/2">
              <svg
                viewBox="0 0 14 400"
                preserveAspectRatio="none"
                className="h-full w-3.5 overflow-visible"
                aria-hidden="true"
              >
                <defs>
                  <filter id="rough-line-v" x="-50%" y="-5%" width="200%" height="110%">
                    <feTurbulence
                      type="turbulence"
                      baseFrequency="0.08 0.04"
                      numOctaves={3}
                      seed={17}
                      stitchTiles="stitch"
                      result="noise"
                    />
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="noise"
                      scale={4}
                      xChannelSelector="R"
                      yChannelSelector="G"
                    />
                  </filter>
                  <linearGradient id="line-grad-v" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2F9E34" />
                    <stop offset="50%" stopColor="#852E85" />
                    <stop offset="100%" stopColor="#99F66E" />
                  </linearGradient>
                </defs>
                {/* Track */}
                <rect
                  x="5"
                  y="0"
                  width="4"
                  height="400"
                  rx="2"
                  fill="rgba(255,255,255,0.08)"
                  filter="url(#rough-line-v)"
                />
                {/* Progress fill */}
                <rect
                  x="5"
                  y="0"
                  width="4"
                  height={400 * lineProgress}
                  rx="2"
                  fill="url(#line-grad-v)"
                  filter="url(#rough-line-v)"
                  style={{ transition: 'height 0.1s linear' }}
                />
              </svg>
            </div>
            {STEPS.map((item, i) => {
              const active = progress >= nodeThresholds[i]
              return (
                <div key={item.step} className="flex items-start gap-5">
                  {/* Node circle */}
                  <div
                    className="relative z-20 flex size-11 shrink-0 items-center justify-center rounded-full border-2 transition-all duration-500"
                    style={{
                      borderColor: active ? '#2F9E34' : 'rgba(255,255,255,0.15)',
                      backgroundColor: active ? '#1a1a0e' : '#1a0e20',
                      boxShadow: active
                        ? '0 0 20px rgba(47,158,52,0.3), 0 0 40px rgba(47,158,52,0.1)'
                        : 'none',
                    }}
                  >
                    <div
                      className="absolute inset-0 rounded-full transition-opacity duration-500"
                      style={{
                        backgroundColor: 'rgba(47,158,52,0.15)',
                        opacity: active ? 1 : 0,
                      }}
                    />
                    <item.icon
                      className="relative z-10 size-5 transition-colors duration-500"
                      style={{ color: active ? '#99F66E' : 'rgba(255,255,255,0.3)' }}
                      aria-hidden="true"
                    />
                  </div>

                  {/* Text */}
                  <div className="pt-1">
                    <span
                      className="font-heading text-base font-bold tracking-wide uppercase transition-colors duration-500"
                      style={{ color: active ? '#e995e8' : 'rgba(255,255,255,0.25)' }}
                    >
                      Step {item.step}
                    </span>
                    <h3
                      className="font-heading text-lg font-bold transition-colors duration-500"
                      style={{ color: active ? '#ffffff' : 'rgba(255,255,255,0.4)' }}
                    >
                      {item.label}
                    </h3>
                    <p
                      className="mt-0.5 text-base transition-colors duration-500"
                      style={{
                        color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.2)',
                      }}
                    >
                      {item.detail}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
